// src/lib/api_campaigns.ts
// Cliente para el módulo Marketing — Campaigns (Meta Ads).
// Mirrors `apps/campaigns/` routers/schemas on the Django backend:
// /admin/campaigns/**, /admin/campaign-creatives/**, /admin/campaign-audiences/**
//
// Reuses the /django-api proxy + JWT pattern from api_travel.ts.

const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function authHeaders(): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getJwt()}`,
  };
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) { headers['Content-Type'] = 'application/json'; }
  const url = `${DJANGO_PROXY}${path}`;
  const res = await fetch(url, { ...init, headers: { ...headers, ...(init?.headers || {}) } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    if (res.status === 401) throw new ApiError('Sesión expirada. Inicia sesión nuevamente.', 401);
    if (res.status === 403) throw new ApiError(detail || 'Sin permisos para esta acción.', 403);
    if (res.status === 404) throw new ApiError(detail || 'Recurso no encontrado.', 404);
    if (res.status === 422) throw new ApiError(detail || 'Error de validación. Revisa los campos.', 422);
    if (res.status === 502) throw new ApiError('Meta no responde — reintenta en un momento.', 502);
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// ═══════════════════════════════════════════════════════════════
// Types — mirror apps/campaigns/schemas.py
// ═══════════════════════════════════════════════════════════════

export interface Paginated<T> { items: T[]; count: number }

export type CampaignStatus =
  | 'draft' | 'pending' | 'active' | 'paused' | 'completed' | 'error';

export type CampaignObjective =
  | '' | 'AWARENESS' | 'TRAFFIC' | 'ENGAGEMENT' | 'LEADS'
  | 'APP_PROMOTION' | 'SALES' | 'MESSAGES';

export interface Campaign {
  id: number;
  org_id: string;
  name: string;
  objective: CampaignObjective;
  status: CampaignStatus;
  meta_campaign_id: string;
  ad_account_name: string;
  daily_budget_cents: number | null;
  lifetime_budget_cents: number | null;
  start_date: string | null;
  end_date: string | null;
  targeting: Record<string, unknown>;
  ai_optimized: boolean;
  is_favorite: boolean;
  is_boost: boolean;
  boosted_post_meta_id: string;
  total_spend_cents: number;
  total_impressions: number;
  total_clicks: number;
  total_conversions: number;
  error_message: string;
  created_at: string;
  updated_at: string;
}

export interface AdSet {
  id: number;
  campaign_id: number;
  name: string;
  status: CampaignStatus;
  meta_adset_id: string;
  targeting: Record<string, unknown>;
  placement: Record<string, unknown>;
  bid_strategy: string;
  daily_budget_cents: number | null;
  created_at: string;
  updated_at: string;
}

export interface Ad {
  id: number;
  ad_set_id: number;
  name: string;
  status: CampaignStatus;
  meta_ad_id: string;
  creative_id: number | null;
  impressions: number;
  clicks: number;
  spend_cents: number;
  conversions: number;
  messaging_conversations: number;
  ctr: number | null;
  cpc_cents: number | null;
  created_at: string;
  updated_at: string;
}

export interface AdSetDetail extends AdSet {
  ads: Ad[];
}

export interface CampaignDetail extends Campaign {
  ad_sets: AdSetDetail[];
  performance_history: Array<{
    date: string;
    spend_cents: number;
    impressions: number;
    clicks: number;
    conversions: number;
  }>;
}

export interface CampaignCreateIn {
  name: string;
  objective?: CampaignObjective;
  status?: CampaignStatus;
  daily_budget_cents?: number | null;
  lifetime_budget_cents?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  targeting?: Record<string, unknown>;
  ai_optimized?: boolean;
  is_boost?: boolean;
  boosted_post_meta_id?: string;
}

export interface CampaignUpdateIn {
  name?: string;
  objective?: CampaignObjective;
  status?: CampaignStatus;
  daily_budget_cents?: number | null;
  lifetime_budget_cents?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  targeting?: Record<string, unknown>;
  ai_optimized?: boolean;
  is_favorite?: boolean;
  boosted_post_meta_id?: string;
}

export interface CampaignActionOut {
  campaign_id: number;
  task_id?: string | null;
  status: 'queued' | 'skipped';
  detail?: string;
}

export interface BudgetAdjustIn {
  daily_budget_cents?: number | null;
  lifetime_budget_cents?: number | null;
}

export interface CampaignDailyMetric {
  id: number;
  campaign_id: number;
  date: string;               // YYYY-MM-DD
  spend_cents: number;
  cpc_cents: number | null;
  cpm_cents: number | null;
  impressions: number;
  clicks: number;
  reach: number;
  conversions: number;
  ctr: number | null;
  frequency: number | null;
  actions: Array<Record<string, unknown>>;
}

export interface ListCampaignsFilters {
  status?: CampaignStatus;
  objective?: CampaignObjective;
  is_favorite?: boolean;
  is_boost?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
}

// ═══════════════════════════════════════════════════════════════
// Query-string helper
// ═══════════════════════════════════════════════════════════════

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// ═══════════════════════════════════════════════════════════════
// Campaigns
// ═══════════════════════════════════════════════════════════════

export async function listCampaigns(
  filters: ListCampaignsFilters = {},
): Promise<Paginated<Campaign>> {
  const { limit = 25, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<Campaign>>(
    `/api/v1/admin/campaigns/${qs({ ...rest, limit, offset })}`,
  );
}

export async function getCampaign(id: number): Promise<CampaignDetail> {
  return djangoFetch<CampaignDetail>(`/api/v1/admin/campaigns/${id}`);
}

export async function createCampaign(data: CampaignCreateIn): Promise<Campaign> {
  return djangoFetch<Campaign>('/api/v1/admin/campaigns/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCampaign(
  id: number,
  data: CampaignUpdateIn,
): Promise<Campaign> {
  return djangoFetch<Campaign>(`/api/v1/admin/campaigns/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCampaign(id: number): Promise<void> {
  await djangoFetch<void>(`/api/v1/admin/campaigns/${id}`, { method: 'DELETE' });
}

// ── Actions (async tasks — returns {status: queued, task_id}) ──

export async function launchCampaign(id: number): Promise<CampaignActionOut> {
  return djangoFetch<CampaignActionOut>(`/api/v1/admin/campaigns/${id}/launch`, {
    method: 'POST',
    body: '{}',
  });
}

export async function pauseCampaign(id: number): Promise<CampaignActionOut> {
  return djangoFetch<CampaignActionOut>(`/api/v1/admin/campaigns/${id}/pause`, {
    method: 'POST',
    body: '{}',
  });
}

export async function resumeCampaign(id: number): Promise<CampaignActionOut> {
  return djangoFetch<CampaignActionOut>(`/api/v1/admin/campaigns/${id}/resume`, {
    method: 'POST',
    body: '{}',
  });
}

export async function adjustBudget(
  id: number,
  data: BudgetAdjustIn,
): Promise<CampaignActionOut> {
  return djangoFetch<CampaignActionOut>(`/api/v1/admin/campaigns/${id}/budget`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function syncCampaign(
  id: number,
  date_preset: string = 'last_7d',
): Promise<CampaignActionOut> {
  return djangoFetch<CampaignActionOut>(
    `/api/v1/admin/campaigns/${id}/sync${qs({ date_preset })}`,
    { method: 'POST', body: '{}' },
  );
}

export async function refreshCampaignStates(): Promise<{
  status: string; org_id?: string; remote_campaigns?: number; updated?: number; reason?: string;
}> {
  return djangoFetch(`/api/v1/admin/campaigns/refresh-states`, {
    method: 'POST', body: '{}',
  });
}

// ── Metrics (for the detail view's charts) ──

export async function listCampaignMetrics(
  id: number,
  params: { days?: number; start_date?: string; end_date?: string } = {},
): Promise<CampaignDailyMetric[]> {
  return djangoFetch<CampaignDailyMetric[]>(
    `/api/v1/admin/campaigns/${id}/metrics${qs(params)}`,
  );
}

// ═══════════════════════════════════════════════════════════════
// Boost flow (A4)
// ═══════════════════════════════════════════════════════════════

export interface PagePost {
  id: string;                     // "{page_id}_{post_id}"
  message_preview: string;        // truncated ≤140 chars
  message: string;                // full caption
  created_time: string;           // ISO
  thumbnail_url: string;
  permalink_url: string;
  likes_count: number;
  comments_count: number;
  shares_count: number;
}

export type BoostDestinationKind =
  | 'awareness' | 'whatsapp' | 'messenger' | 'phone_call' | 'website';

export interface BoostCreateIn {
  page_post_id: string;
  daily_budget_cents: number;
  duration_days?: number;         // default 7 server-side
  name?: string;                  // auto-generated from post id when omitted
  targeting?: Record<string, unknown>;
  /** Texto libre del operator describiendo audiencia. Cuando llega no
   *  vacio, el server lo interpreta via LLM + buscar_*_meta tools y
   *  toma precedencia sobre `targeting` JSON. */
  targeting_text?: string;
  /** Destino del boost — determina objective + optimization + destination_type.
   *  awareness = boost puro (spread post, sin CTA específico). */
  destination_kind?: BoostDestinationKind;
  destination_value?: string;
  start_date?: string;            // ISO; defaults to now server-side
}

export interface AccountMetaOut {
  currency: string;
  country: string;
  ad_account_id: string;
  ad_account_name: string;
}

export async function getAccountMeta(): Promise<AccountMetaOut> {
  return djangoFetch<AccountMetaOut>('/api/v1/admin/campaigns/account-meta');
}

/** Lists the org's recent page posts eligible for boosting. Backend
 *  caches 5 min per org+limit; pass `refresh=true` for a forced reload. */
export async function listPagePosts(
  params: { limit?: number; refresh?: boolean } = {},
): Promise<PagePost[]> {
  return djangoFetch<PagePost[]>(
    `/api/v1/admin/campaigns/boost/page-posts${qs(params)}`,
  );
}

/** One-shot create + queue: returns the created Campaign (is_boost=true).
 *  The publish side-effect is async — poll the detail view's status to see
 *  it move from `pending` → `paused` (start-paused) once Meta accepts. */
export async function createBoost(data: BoostCreateIn): Promise<Campaign> {
  return djangoFetch<Campaign>('/api/v1/admin/campaigns/boost', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ═══════════════════════════════════════════════════════════════
// CM-3a/b/c — Campaign Director (agentic)
// ═══════════════════════════════════════════════════════════════

export type DirectorStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface DirectorAttachment {
  url: string;
  content_type?: string;
  kind?: 'image' | 'video';
  caption?: string;
}

export type ActivityLogEntry =
  | { ts: number; type: 'thought'; text: string }
  | { ts: number; type: 'tool_call'; tool: string; input: Record<string, unknown> }
  | { ts: number; type: 'tool_result'; tool: string; output: unknown; duration_ms: number }
  | { ts: number; type: 'error'; where: string; error: string };

export type DirectorDestinationKind =
  | 'auto' | 'whatsapp' | 'messenger' | 'phone_call' | 'website' | 'lead_form';

export interface DirectorRequest {
  id: number;
  org_id: string;
  status: DirectorStatus;
  mode: 'agentic' | 'classic';
  instruction: string;
  attachments: DirectorAttachment[];
  activity_log: ActivityLogEntry[];
  llm_model: string;
  final_response: Record<string, unknown>;
  started_at: string | null;
  completed_at: string | null;
  error_message: string;
  destination_kind?: DirectorDestinationKind;
  destination_value?: string;
}

export interface DirectorRequestIn {
  instruction: string;
  mode?: 'agentic' | 'classic';
  attachments?: DirectorAttachment[];
  destination_kind?: DirectorDestinationKind;
  destination_value?: string;
}

export interface MaterializeAdSummary {
  id: number;
  name: string;
  headline: string;
  body: string;
  cta: string;
  link_url: string;
  image_url: string;
  video_url: string;
}

export interface MaterializeAdsetSummary {
  id: number;
  name: string;
  age_min: number | null;
  age_max: number | null;
  daily_budget_minor: number | null;
  cities: string[];
  countries: string[];
  interests: string[];
  behaviors: string[];
  ads: MaterializeAdSummary[];
}

export interface MaterializeOut {
  campaign_id: number;
  name: string;
  objective: string;
  status: string;
  currency: string;
  daily_budget_minor: number | null;
  lifetime_budget_minor: number | null;
  start_date: string | null;
  end_date: string | null;
  adsets_created: number;
  destination_kind: DirectorDestinationKind;
  destination_value: string;
  adsets: MaterializeAdsetSummary[];
  notes?: string;  // Aviso si el LLM consolidó adsets (sub-budget, etc.)
}

export type MaterializeJobStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface MaterializeJobOut {
  job_id: number;
  request_id: number;
  status: MaterializeJobStatus;
  error_message: string;
  started_at: string | null;
  finished_at: string | null;
  result: MaterializeOut | null;
}

/**
 * Dispara materialize ASYNC: devuelve job_id + status=pending. El caller
 * debe poll getMaterializeJob(job_id) hasta status ∈ {completed, failed}.
 * Convertido a async porque el flow sync excedía timeout 60s de Traefik.
 */
export async function materializeDirectorRequest(id: number): Promise<MaterializeJobOut> {
  return djangoFetch<MaterializeJobOut>(
    `/api/v1/admin/campaigns/director/requests/${id}/materialize`,
    { method: 'POST', body: '{}' },
  );
}

export async function getMaterializeJob(jobId: number): Promise<MaterializeJobOut> {
  return djangoFetch<MaterializeJobOut>(
    `/api/v1/admin/campaigns/director/materialize-jobs/${jobId}`,
  );
}

export async function createDirectorRequest(
  data: DirectorRequestIn,
): Promise<DirectorRequest> {
  return djangoFetch<DirectorRequest>(
    '/api/v1/admin/campaigns/director/requests',
    { method: 'POST', body: JSON.stringify(data) },
  );
}

export async function listDirectorRequests(
  status?: DirectorStatus,
): Promise<DirectorRequest[]> {
  const suffix = status ? `?status=${status}` : '';
  const res = await djangoFetch<DirectorRequest[] | { items: DirectorRequest[] }>(
    `/api/v1/admin/campaigns/director/requests${suffix}`,
  );
  if (Array.isArray(res)) return res;
  return res.items ?? [];
}

export async function getDirectorRequest(id: number): Promise<DirectorRequest> {
  return djangoFetch<DirectorRequest>(
    `/api/v1/admin/campaigns/director/requests/${id}`,
  );
}

export async function cancelDirectorRequest(id: number): Promise<DirectorRequest> {
  return djangoFetch<DirectorRequest>(
    `/api/v1/admin/campaigns/director/requests/${id}/cancel`,
    { method: 'POST' },
  );
}
